import axios from 'axios';
import _, { get } from 'lodash';
import React, {
  createContext,
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import TenantBusiness from '../interfaces/TenantBusiness';
import UserType from '../interfaces/User';
import { TenantConfigTypes } from '../shared/enums/TenantConfigs';
import useAppTheme from '../hooks/useAppTheme';
import { updateVariable } from '../utils/cssVariable';
import formatNumber from '../utils/formatNumber';

const SESSION_BUSINESS_KEY = "selectedBusinessId";
const SEARCH_PARAM_BUSINESS_KEY = "bu";

interface UserContextType {
  currentUser: UserType;
  businesses: TenantBusiness[];
  selectedBusiness: TenantBusiness;
  userPermissions: string[];
  subscribedPermissions: string[];
  businessUnitNameMap: { [key: string]: TenantBusiness };
  setBusiness: (businessId: number) => void;
  getTenantLevelConfig: <T = Record<string, any>>(
    configType: TenantConfigTypes
  ) => T | null;
  formatCurrency: (
    currency: number,
    options?: Intl.NumberFormatOptions,
    showSymbol?: boolean,
  ) => string;
  disableBusinessSwitch: boolean;
  setDisableBusinessSwitch: (value: boolean) => void;
  setSelectedBusiness: React.Dispatch<React.SetStateAction<TenantBusiness>>;
}

const UserContext = createContext<UserContextType>({
  currentUser: {} as any,
  businesses: [],
  selectedBusiness: {} as any,
  userPermissions: [],
  subscribedPermissions: [],
  businessUnitNameMap: {},
  setBusiness(businessId: number) {},
  formatCurrency(currency: number) {
    return '';
  },
  getTenantLevelConfig(_configType: TenantConfigTypes) {
    return null;
  },
  disableBusinessSwitch: false,
  setDisableBusinessSwitch(value: boolean) {},
  setSelectedBusiness() {},
});

export const UserProvider: FC<
  React.PropsWithChildren & { currentUser: UserType }
> = ({ currentUser, children }) => {
  const { setPalette } = useAppTheme();
  const businesses = useMemo(
    () =>
      currentUser.tenant.brands.reduce((acc, val) => {
        acc.push(...val.businesses);
        return acc;
      }, [] as TenantBusiness[]),
    [currentUser]
  );

  const [selectedBusiness, setSelectedBusiness] = useState<TenantBusiness>(() => {
    const sessionBusinessId =
      getBusinessIDFromSearchParams() || getBusinessIDFromSession();

    if (sessionBusinessId) {
      const business = getBusinessById(sessionBusinessId);
      if (business) return business;
    }
    return businesses[0];
  });
  useEffect(() => {
    const sessionBusinessId =
      getBusinessIDFromSearchParams() || getBusinessIDFromSession();

    let businessToSelect: TenantBusiness | undefined;

    if (sessionBusinessId) {
      businessToSelect = getBusinessById(sessionBusinessId);
    }
    if (!businessToSelect) {
      businessToSelect = businesses[0];
    }
    if (businessToSelect && businessToSelect.id !== selectedBusiness?.id) {
      setSelectedBusiness(businessToSelect);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, businesses]);

  useLayoutEffect(() => {
    if (selectedBusiness) {
      axios.defaults.headers.common['Business-Id'] = selectedBusiness.id;
      axios.defaults.headers.common['Business-Name'] = selectedBusiness.name;
    }
   
  }, [selectedBusiness]);

  const [disableBusinessSwitch, setDisableBusinessSwitch] =
    useState<boolean>(false);

  useEffect(() => {
    if (selectedBusiness) {
      selectedBusiness.id !== getBusinessIDFromSession() &&
        saveBusinessIDInSession(selectedBusiness.id);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    const tenantConfigDetails = currentUser.tenant.tenantLevelConfigs.find(
      ({ configType }) => configType === TenantConfigTypes.Theme
    );

    if (tenantConfigDetails && tenantConfigDetails.configDetails) {
      setPalette(tenantConfigDetails.configDetails);
      updateVariable(
        'primary-main',
        tenantConfigDetails.configDetails.primary.main
      );
      updateVariable(
        'primary-light',
        tenantConfigDetails.configDetails.primary.light
      );
      updateVariable(
        'primary-dark',
        tenantConfigDetails.configDetails.primary.dark
      );
      updateVariable(
        'primary-contrast-text',
        tenantConfigDetails.configDetails.primary.contrastText
      );
    }
  }, [currentUser.tenant.tenantLevelConfigs, setPalette]);

  const subscribedPermissions = useMemo<string[]>(
    () => get(selectedBusiness, "subscribedPermissions", []) as string[],
    [selectedBusiness],
  );

  const userPermissions = useMemo(() => {
    const rolePermissions =
      currentUser?.tenant_role_groups.reduce((acc, val) => {
        if (_.isArray(val.permissions)) {
          acc.push(...val.permissions);
          return acc;
        }

        const permissions = Object.values(val.permissions).reduce(
          (accp, { enabled }) => accp.concat(enabled),
          [] as string[],
        );

        acc.push(...permissions);

        return acc;
      }, [] as string[]) || [];
    // Fallback: Use rolePermissions when subscribedPermissions is empty.
    // This is a temporary workaround during incremental tenant onboarding.
    // TODO: Remove this fallback once all tenants have been onboarded.
    const userPermissions =
      subscribedPermissions.length === 0
        ? rolePermissions
        : subscribedPermissions.filter((permission) =>
            rolePermissions.includes(permission),
          );
    return userPermissions;
  }, [currentUser, subscribedPermissions]);

  const businessUnitNameMap = useMemo(() => {
    return (
      currentUser?.tenant.brands.reduce((acc, val) => {
        val.businesses.forEach((business) => {
          acc[business.name] = business;
        });

        return acc;
      }, {} as { [key: string]: TenantBusiness }) || {}
    );
  }, [currentUser]);

  const handleBusinessChange = (businessId: number) => {
    const business = getBusinessById(businessId);

    if (!business) {
      throw new Error(`No Business found for id ${businessId}`);
    } else setSelectedBusiness(business);
  };

  function getBusinessById(businessId: number): TenantBusiness | undefined {
    return businesses.find(({ id }) => id === businessId);
  }

  function getTenantLevelConfig<T = Record<string, any>>(
    _configType: TenantConfigTypes
  ) {
    const tenantConfigDetails = currentUser.tenant.tenantLevelConfigs.find(
      ({ configType }) => configType === _configType
    );
    if (!tenantConfigDetails) {
      return null;
    }
    return tenantConfigDetails.configDetails as T;
  }

  const formatCurrency = (
    currency: number,
    options: Intl.NumberFormatOptions = {},
    showSymbol = true,
  ) => {
    return formatNumber(currency, {
      currency: selectedBusiness.country,
      style: showSymbol ? "currency" : "decimal",
      notation: 'compact',
      maximumFractionDigits: 2,
      ...options,
    });
  };

  return (
    <UserContext.Provider
      value={{
        currentUser,
        businesses,
        selectedBusiness,
        userPermissions,
        subscribedPermissions,
        businessUnitNameMap,
        setBusiness: handleBusinessChange,
        getTenantLevelConfig,
        formatCurrency,
        disableBusinessSwitch,
        setDisableBusinessSwitch,
        setSelectedBusiness,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;

const getBusinessIDFromSession = () => {
  const selectedBusiness = sessionStorage.getItem(SESSION_BUSINESS_KEY);
  return selectedBusiness ? +selectedBusiness : null;
};

const getBusinessIDFromSearchParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.has(SEARCH_PARAM_BUSINESS_KEY)
    ? +searchParams.get(SEARCH_PARAM_BUSINESS_KEY)!
    : null;
};

const saveBusinessIDInSession = (businessId: number) => {
  return sessionStorage.setItem(SESSION_BUSINESS_KEY, businessId.toString());
};
